<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";
import trim from "lodash-es/trim";

import { toast } from "@/common/utils/toast";
import { getDate, getHour } from "@/common/utils/dates";

import {
  versionRelatedList,
  versionRelatedListId,
  versionRelatedCreate,
  versionRelatedDelete,
} from "@/common/api/rest/common/version";

import { gameList } from "@/common/api/rest/game/game";
import { accessoryList } from "@/common/api/rest/accessory/accessory";
import { consoleList } from "@/common/api/rest/console/console";

import type {
  VersionOwnerType,
  VersionRelatedType,
  VersionRelationType,
  VersionTargetType,
} from "@/common/types/common/version";

import {
  baseAvatar,
  baseConfirm,
  baseLoading,
  baseInput,
  baseModal,
  baseSearchExact,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const props = defineProps<{
  ownerType: VersionOwnerType;
  ownerId: string;
  relationType: VersionRelationType;
}>();

const related = ref<VersionRelatedType[]>([]);
const relatedIds = ref<
  { item_id: string; target_type: VersionTargetType; target_id: string }[]
>([]);
const selected = ref<VersionRelatedType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(true);
const showModalAddRelated = ref(false);
const showModalRemoveRelated = ref(false);

const targetSearchListRef = ref<any>(null);
const targetSearch = ref<string>("");
const targetExactSearch = ref<boolean>(false);
const targetSearchList = ref<any[]>([]);
const targetLimit = ref<number>(10);
const targetPage = ref<number>(0);
const targetPages = ref<number>(0);
const targetSearched = ref<number>(0);
const targetTotal = ref<number>(0);
const loadingMoreTargets = ref(false);

const targetType = computed<VersionTargetType>(() =>
  props.relationType === "console_memory" ? "console" : props.ownerType,
);

const sectionLabel = computed(() => {
  const labels: Record<VersionRelationType, string> = {
    this_console: t("collection.labels.version_this_console"),
    other_systems: t("collection.labels.version_other_systems"),
    compilation: t("collection.labels.version_compilation"),
    console_memory: t("collection.labels.version_console_memory"),
    unofficial_other_systems: t(
      "collection.labels.version_unofficial_other_systems",
    ),
  };

  return labels[props.relationType];
});

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "created", label: t("common.labels.created") },
]);

function targetId(item: any): string {
  if (targetType.value === "console") {
    return item.console_id;
  }

  if (targetType.value === "accessory") {
    return item.accessory_id;
  }

  return item.game_id;
}

async function fetchRelated() {
  await versionRelatedList(props.ownerType, props.ownerId, {
    page: page.value,
    limit: limit.value,
    search: search.value,
    sort: "DESC",
    relation_type: props.relationType,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        related.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchRelatedId() {
  await versionRelatedListId(
    props.ownerType,
    props.ownerId,
    props.relationType,
  )
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        relatedIds.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function searchTargets(append = false) {
  if (
    append &&
    (loadingMoreTargets.value || targetPage.value + 1 >= targetPages.value)
  ) {
    return;
  }

  if (append) {
    loadingMoreTargets.value = true;
  }

  const pageToFetch = append ? targetPage.value + 1 : 0;
  const query = {
    page: pageToFetch,
    limit: targetLimit.value,
    search: targetSearch.value,
    match: targetExactSearch.value ? ("exact" as const) : ("like" as const),
    sort: "ASC" as const,
  };

  const request =
    targetType.value === "console"
      ? consoleList(query)
      : targetType.value === "accessory"
        ? accessoryList(query)
        : gameList(query);

  await request
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newItems = (data.list ?? []).filter(
          (item: any) =>
            targetId(item) !== props.ownerId ||
            targetType.value !== props.ownerType,
        );
        targetSearchList.value = append
          ? [...targetSearchList.value, ...newItems]
          : newItems;
        targetPage.value = pageToFetch;
        targetPages.value = data.infos.pages;
        targetTotal.value = data.infos.total;
        targetSearched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loadingMoreTargets.value = false;
    });
}

function changePage(value: number) {
  page.value = value;
  fetchRelated();
}

function changeLimit(value: number) {
  limit.value = value;
  page.value = 0;
  fetchRelated();
}

function searchItem(value: string) {
  search.value = value;

  fetchRelated();
}

function resetSearchTarget() {
  targetSearch.value = "";
  targetExactSearch.value = false;
  targetSearchList.value = [];
  targetPage.value = 0;
  targetPages.value = 0;
  targetSearched.value = 0;
  targetTotal.value = 0;
  loadingMoreTargets.value = false;
}

async function addRelated() {
  store.commit("SET_LOADING", true);

  resetSearchTarget();
  await searchTargets();
  store.commit("SET_LOADING", false);

  showModalAddRelated.value = true;
}

async function searchTarget() {
  targetSearchList.value = [];
  targetPage.value = 0;
  targetPages.value = 0;
  targetSearched.value = 0;
  targetTotal.value = 0;

  await searchTargets();
}

function onTargetListScroll(event: Event) {
  const target =
    (event.target as HTMLElement | null) ?? targetSearchListRef.value;

  if (!target) {
    return;
  }

  const reachedEnd =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

  if (
    reachedEnd &&
    !loadingMoreTargets.value &&
    targetPage.value + 1 < targetPages.value
  ) {
    void searchTargets(true);
  }
}

async function selectTarget(item: any) {
  const id = targetId(item);

  if (!relatedIds.value.some((r) => r.target_id === id)) {
    await versionRelatedCreate(props.ownerType, props.ownerId, {
      relation_type: props.relationType,
      target_type: targetType.value,
      target_id: id,
    })
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  } else {
    const relation = relatedIds.value.find((r) => r.target_id === id);

    if (!relation) {
      return;
    }

    await versionRelatedDelete(relation.item_id)
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchRelatedId();
          fetchRelated();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  }
}

function removeRelated(item: VersionRelatedType) {
  selected.value = item;
  showModalRemoveRelated.value = true;
}

function removeRelatedCancel() {
  selected.value = null;
  showModalRemoveRelated.value = false;
}

async function removeRelatedConfirm() {
  if (!selected.value) {
    return;
  }

  await versionRelatedDelete(selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchRelatedId();
        fetchRelated();
        showModalRemoveRelated.value = false;
        selected.value = null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => targetSearch.value,
  () => {
    if (trim(targetSearch.value).length === 0) {
      searchTarget();
    }
  },
);

watch(
  () => targetExactSearch.value,
  () => {
    if (trim(targetSearch.value).length > 0) {
      searchTarget();
    }
  },
);

watch(
  () => props.ownerId,
  async () => {
    await fetchRelated();
    await fetchRelatedId();
  },
);

onMounted(async () => {
  await fetchRelated();
  await fetchRelatedId();
  loading.value = false;
});
</script>

<template>
  <section class="tab-version">
    <div class="tab-header">
      <svg-icon icon="layer" size="2rem" />
      {{ sectionLabel }}
    </div>

    <div class="tab-content">
      <template v-if="loading">
        <div class="empty-data">
          <base-loading />
        </div>
      </template>
      <template v-else>
        <base-table
          :header="tableHeader"
          :data="related"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :copy="false"
          :edit="false"
          :del="true"
          :show-limit="true"
          @change:page="changePage($event)"
          @change:limit="changeLimit($event)"
          @searching="searchItem($event)"
          @btn:add="addRelated"
          @btn:del="removeRelated($event)"
        >
          <template #avatar="{ data }">
            <base-avatar
              position="center"
              :is-edit="false"
              :show-image="true"
              :url="data?.avatar?.large"
              :aspect-ratio="1.333"
              size="3rem"
              border-radius="0.25rem"
            />
          </template>

          <template #created="{ data }">
            <span>
              {{ getDate(data.created) }} - {{ getHour(data.created) }}
            </span>
          </template>
        </base-table>
      </template>
    </div>
  </section>

  <base-confirm
    :show="showModalRemoveRelated"
    :title="t('collection.modals.titles.del_version').toUpperCase()"
    :message="
      t('collection.modals.messages.del_version', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeRelatedCancel()"
    @confirm="removeRelatedConfirm()"
  />

  <base-modal
    :show="showModalAddRelated"
    :title="t('collection.modals.titles.add_version').toUpperCase()"
    @close="showModalAddRelated = false"
  >
    <template #content>
      <div class="target-add">
        <div class="target-search">
          <base-input
            v-model="targetSearch"
            background="#fff"
            color="#000"
            :placeholder="t('common.labels.search')"
            @key:enter="searchTarget()"
          >
            <template #sufix>
              <div class="icon">
                <svg-icon icon="search" size="1.75rem" @click="searchTarget()" />
              </div>
            </template>
          </base-input>
        </div>

        <base-search-exact v-model="targetExactSearch" />

        <div
          ref="targetSearchListRef"
          class="target-list"
          @scroll="onTargetListScroll"
        >
          <template v-for="(item, index) in targetSearchList" :key="index">
            <div class="target">
              <base-avatar
                position="center"
                :is-edit="false"
                :show-image="true"
                :url="item?.avatar?.large"
                :aspect-ratio="1.333"
                size="3rem"
                border-radius="0.25rem"
              ></base-avatar>

              <div class="name">
                {{ item.name }}
              </div>
              <label
                class="custom-checkbox"
                :for="`version-${relationType}-${targetId(item)}`"
              >
                <input
                  :id="`version-${relationType}-${targetId(item)}`"
                  class="checkbox-input"
                  type="checkbox"
                  @change="selectTarget(item)"
                  :checked="
                    relatedIds.some((r) => r.target_id === targetId(item))
                  "
                />
                <span class="checkbox-mark"></span>
              </label>
            </div>
          </template>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<style lang="scss" scoped>
.tab-version {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  gap: 1rem;

  .tab-header {
    display: inline-flex;
    gap: 0.5rem;
    font-size: 1.2rem;
    align-items: center;
    padding-block-end: 0.75rem;
    text-transform: uppercase;
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .empty-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding-block: 2rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }
  }
}

.target-add {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .target-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: scroll;
    max-height: 20rem;
    height: 20rem;
    padding: 0.5rem;
    background: white;
    border-radius: 0.5rem;

    .target {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
      padding-block: 0.5rem;
      padding-inline: 1rem;
      background: #f1f1f1;
      border-radius: 0.5rem;

      .custom-checkbox {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-inline-start: auto;
        cursor: pointer;
      }

      .checkbox-input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      .checkbox-mark {
        width: 1.1rem;
        height: 1.1rem;
        border: 0.125rem solid var(--primary);
        border-radius: 0.25rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        background: white;
      }

      .checkbox-input:checked + .checkbox-mark {
        background: var(--primary);
        border-color: var(--primary);
      }

      .checkbox-input:checked + .checkbox-mark::after {
        content: "";
        width: 0.3rem;
        height: 0.6rem;
        border: solid white;
        border-width: 0 0.125rem 0.125rem 0;
        transform: rotate(45deg);
        margin-top: -0.1rem;
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-version {
    .tab-content {
      padding-inline: 0;
    }
  }
}
</style>
